import { ExpeditionStatusEnum, ShipmentStateCodeEnum, SupplierNotificationStatusEnum, SupplierNotificationTypeEnum } from '@api/ringil-api.v2.tsx';

/** The backend accepts 1–10 ids; merging a single shipment would be a no-op. */
export const MAX_MERGED_EXPEDITIONS = 10;

/**
 * Shipments that can still be folded into another one.
 *
 * Up to and including an assigned carrier. An assigned shipment brings its carrier and its price
 * with it, and the merge carries both over as a direct assignment rather than starting the carrier
 * selection again — see `mergeDistribution`. What stays out is everything from the moment the
 * transport is under way: there is no sense in replacing a shipment a driver is already loading.
 */
const MERGEABLE_STATUSES: ExpeditionStatusEnum[] = [
  ExpeditionStatusEnum.IMPORTED,
  ExpeditionStatusEnum.NEW,
  ExpeditionStatusEnum.ASSIGNING,
  ExpeditionStatusEnum.ASSIGNED,
];

export const isMergeableExpedition = (expedition: { expeditionStatus?: ExpeditionStatusEnum }): boolean => MERGEABLE_STATUSES.includes(expedition.expeditionStatus);

/** A shipment as either the list row or the mass-action shape has it; both name their id differently. */
interface MergeCandidate {
  expeditionStatus?: ExpeditionStatusEnum;
  applicationId?: string;
  expeditionId?: string;
  id?: string;
}

/** What to call a shipment in a message: its reference if it has one, its id only as a last resort. */
const nameOf = (expedition: MergeCandidate): string => expedition.applicationId || expedition.expeditionId || expedition.id || '';

/**
 * Why this selection cannot be merged, or nothing when it can.
 *
 * A greyed-out button with no explanation leaves the user to guess which of their shipments is the
 * problem — so the shipments in the way are named, not just counted. One reason at a time, the most
 * basic first: being told the selection is too large is no use while it is also too small.
 */
export const mergeDisabledReason = (selected: MergeCandidate[], tr: (key: string, fallback: string, data?: object) => string): string | undefined => {
  if (selected.length < 2) return tr('AllExpedition.mergeNeedsTwo', 'Select at least two shipments to merge.');

  if (selected.length > MAX_MERGED_EXPEDITIONS)
    return tr('AllExpedition.mergeTooMany', 'At most {{max}} shipments can be merged at once; {{count}} are selected.', {
      max: MAX_MERGED_EXPEDITIONS,
      count: selected.length,
    });

  const blocked = selected.filter(expedition => !isMergeableExpedition(expedition));
  if (blocked.length > 0)
    return tr('AllExpedition.mergeNotMergeableStatus', 'Only shipments not yet handed to a carrier can be merged. These cannot: {{shipments}}', {
      shipments: blocked
        .map(nameOf)
        .filter(name => name.length > 0)
        .join(', '),
    });

  return undefined;
};

/**
 * One entity the create form is replacing. Both kinds travel together because a single merge may
 * close shipments and supplier notifications in the same request, and the confirm dialog has to
 * split them apart again for the two id lists the backend expects.
 */
export interface MergeSource {
  kind: 'expedition' | 'supplierNotification';
  id: string;
  applicationId?: string;
}

/** A supplier notification as the list row has it; only the fields the rules look at. */
interface SnMergeCandidate {
  id?: string;
  applicationId?: string;
  isArchived?: boolean;
  supplierNotificationStatus?: SupplierNotificationStatusEnum;
  shipmentStateCode?: ShipmentStateCodeEnum;
  supplierNotificationType?: SupplierNotificationTypeEnum;
  groupDemandCreated?: unknown;
  mergedToExpedition?: { expeditionApplicationId?: string };
}

/**
 * Supplier notifications that can still become part of a shipment.
 *
 * A DXX notification cannot become a shipment at all, so it cannot be merged into one either — the
 * same rule the create-shipment-from-notification action already applies. A notification that is
 * canceled, archived, still waiting for its goods, or already tied to a group demand has nothing to
 * contribute to a new shipment.
 *
 * One that has already been merged is out: its goods are on the shipment it was merged into, and
 * merging it again would move them a second time.
 *
 * Deliberately allowed: a notification that already has a shipment of its own but has not been
 * merged. That shipment is merged and closed along with it, exactly as if it had been selected
 * directly.
 */
export const isMergeableSupplierNotification = (notification: SnMergeCandidate): boolean =>
  notification.supplierNotificationType === SupplierNotificationTypeEnum.EFXX &&
  notification.supplierNotificationStatus !== SupplierNotificationStatusEnum.CANCELED &&
  notification.isArchived !== true &&
  notification.groupDemandCreated == null &&
  notification.mergedToExpedition == null &&
  notification.shipmentStateCode === ShipmentStateCodeEnum.READY;

const nameOfNotification = (notification: SnMergeCandidate): string => notification.applicationId || notification.id || '';

/**
 * Why this selection of supplier notifications cannot be merged, or nothing when it can.
 *
 * The backend rejects the same selections, but a disabled button naming the notification in the way
 * saves the user a round trip.
 */
export const snMergeDisabledReason = (selected: SnMergeCandidate[], tr: (key: string, fallback: string, data?: object) => string): string | undefined => {
  if (selected.length < 2) return tr('SupplierNotificationList.mergeNeedsTwo', 'Select at least two supplier notifications to merge.');

  if (selected.length > MAX_MERGED_EXPEDITIONS)
    return tr('SupplierNotificationList.mergeTooMany', 'At most {{max}} supplier notifications can be merged at once; {{count}} are selected.', {
      max: MAX_MERGED_EXPEDITIONS,
      count: selected.length,
    });

  const blocked = selected.filter(notification => !isMergeableSupplierNotification(notification));
  if (blocked.length > 0)
    return tr(
      'SupplierNotificationList.mergeNotMergeable',
      'Only ready, non-archived EFXX notifications that have not been merged yet and have no group demand can be merged. These cannot: {{supplierNotifications}}',
      {
        supplierNotifications: blocked
          .map(nameOfNotification)
          .filter(name => name.length > 0)
          .join(', '),
      },
    );

  return undefined;
};
